import { onDisconnect, onValue, ref, serverTimestamp, set } from "firebase/database";

import { UserActivityStatus } from "@fitness-app/data-models/entities/UserProfile";
import { getBrowserName, isMobile } from "@fitness-app/utils/src/users/getBrowserName";
import { getDeviceFingerprint } from "@fitness-app/utils/src/users/getDeviceFingerprint";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AppThunk } from "../../../index";

export const setUserPresence =
  (): AppThunk =>
  async (dispatch, getState, { realtimeDatabase, auth, appVersion, subscriptions }) => {
    try {
      const { id } = await getLoggedUser(auth);
      const userStatusDatabaseRef = ref(realtimeDatabase, `/status/${id}`);
      const presenceRef = ref(realtimeDatabase, ".info/connected");

      if (subscriptions.realtimePresenceChannel) {
        return;
      }

      const device = {
        fingerprint: typeof window !== "undefined" && window ? await getDeviceFingerprint() : null,
        lastSeen: serverTimestamp(),
        type: isMobile() ? "mobile" : "desktop",
        name: `${getBrowserName()} - ${
          // @ts-expect-error ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          navigator.userAgentData?.platform || navigator.platform
        }`,
        version: `${appVersion} (web)`,
      };

      const isOfflineForDatabase = {
        state: UserActivityStatus.Offline,
        lastChanged: serverTimestamp(),
        appVersion: `${appVersion} (web)`,
        device,
      };

      const isOnlineForDatabase = {
        state: UserActivityStatus.Online,
        lastChanged: serverTimestamp(),
        appVersion: `${appVersion} (web)`,
        device,
      };

      subscriptions.realtimePresenceChannel = onValue(presenceRef, (snapshot) => {
        if (snapshot.val() == false) {
          return;
        }

        onDisconnect(userStatusDatabaseRef)
          .set(isOfflineForDatabase)
          .then(() => {
            void set(userStatusDatabaseRef, isOnlineForDatabase);
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.log("Error with user status", e);
          });
      });
    } catch (error) {
      // Silently handle authentication errors during background presence tracking
      // This can occur when user is signed out while presence is being set
      if (error instanceof Error && error.message === "user-is-not-logged") {
        // User is not logged in, silently return without setting presence
        return;
      }
      // Re-throw other errors
      throw error;
    }
  };
